import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import KibliLogo from "../components/KibliLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres.");
    if (password !== confirm) return setError("Las contraseñas no coinciden.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-md text-center space-y-4">
          <KibliLogo size={64} />
          <h1 className="text-2xl font-extrabold">Enlace inválido</h1>
          <p className="text-slate-600">El enlace de restablecimiento no es válido o ha caducado.</p>
          <Link to="/forgot-password" className="btn-primary inline-block px-6 py-3">Solicitar uno nuevo</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md flex flex-col items-center gap-6 animate-fade-in-up">
        <KibliLogo size={64} />
        {done ? (
          <div className="w-full text-center space-y-4" data-testid="reset-done">
            <div className="inline-flex w-16 h-16 rounded-full bg-green-100 text-green-600 items-center justify-center mx-auto">
              <i className="fa-solid fa-circle-check text-2xl"></i>
            </div>
            <h1 className="text-2xl font-extrabold">Contraseña actualizada</h1>
            <p className="text-slate-600">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <p className="text-sm text-slate-400">Redirigiendo al inicio de sesión…</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-extrabold">Nueva contraseña</h1>
              <p className="text-slate-500 mt-1">Elige una contraseña segura para tu cuenta.</p>
            </div>
            <form onSubmit={onSubmit} className="w-full space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="reset-password-input"
                    className="input-base pr-12"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute inset-y-0 right-3 my-auto h-9 w-9 flex items-center justify-center text-slate-500 hover:text-slate-700"
                    aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <i className={`fa-solid ${show ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Mínimo 6 caracteres.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Repite la contraseña</label>
                <input
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  data-testid="reset-confirm-input"
                  className="input-base"
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="reset-error">{error}</div>
              )}
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="btn-primary w-full py-4">
                {loading ? "Guardando..." : "Guardar contraseña"}
              </button>
            </form>
            <Link to="/login" className="text-blue-600 font-semibold text-sm">
              <i className="fa-solid fa-arrow-left mr-1"></i> Volver al inicio de sesión
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
